import { request, getLocale } from '@pb/utils'

import HttpStatusCode from './httpStatusCode'
import {
  FetchStyleboardResponse,
  MyStyleboards,
  StyleboardStatus,
  Author,
  ImageType,
  Styleboard,
  FetchSimilarItemsResponse,
  PutItemToStyleboardRequest,
  PublicStyleboardSort,
} from './types'
import { normaliseArticles, normaliseItemsProductImageSrc, normaliseStyleboards } from './utils'

// We cannot use version or locale in tst or any environment because our api would be https://tst-www2.hm.com/sv_se/v1/styleboardservice/
// and it need to be https://tst-www2.hm.com/styleboardservice/ The backend could not accept wildcards unfortunately so we disable it for stylboard requests
const STYLEBOARD_BASE = `styleboardservice/styleboards`
const PUBLIC_STYLEBOARD_BASE = `styleboardservice/${getLocale()}/v1/styleboards/reco/landing-page`
const MY_STYLEBOARDS_BASE = `styleboardservice/my-styleboard/follows`
const POPULAR_STYLEBOARDS_BASE = `styleboardservice/${getLocale()}/v1/styleboards/popular`
const USERS_BASE = `styleboardservice/users`
const MEDIA_BASE = `styleboardservice/media`
const SIMILAR_ITEMS_BASE = `${STYLEBOARD_BASE}/articles/similar`
const STYLEBOARD_ITEMS = `${STYLEBOARD_BASE}/items`

const extraConfig = {
  use: {
    apiVersion: false,
    locale: false,
  },
  // Since HMCORP_locale cookie is not always set we need to explicitly send it to the backend
  headers: {
    sb_hmcorp_locale: getLocale('mixed'),
  },
}

export const fetchStyleboard = async (
  styleboardId: string,
  link?: string,
): Promise<FetchStyleboardResponse> => {
  const currentLink = link || `${STYLEBOARD_BASE}/${styleboardId}`

  const response = await request.get(currentLink, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  let normalisedReponse

  if (link) {
    normaliseItemsProductImageSrc(response.data.items)
    normalisedReponse = { items: response.data.items }
  } else {
    normaliseStyleboards([response.data])
    normalisedReponse = { styleboard: response.data }
  }

  return normalisedReponse
}

export const fetchStyleboardForAnonymousUser = async (
  styleboardId: string,
  link?: string,
): Promise<FetchStyleboardResponse> => {
  const currentLink = link || `${STYLEBOARD_BASE}/anonymous/${styleboardId}`

  const response = await request.get(currentLink, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  let normalisedReponse

  if (link) {
    normaliseItemsProductImageSrc(response.data.items)
    normalisedReponse = { items: response.data.items }
  } else {
    normaliseStyleboards([response.data])
    normalisedReponse = { styleboard: response.data }
  }

  return normalisedReponse
}

export const fetchMoreData = async (link: string): Promise<FetchStyleboardResponse> => {
  const response = await request.get(link, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  normaliseItemsProductImageSrc(response.data.items)
  return response.data
}

export const fetchStyleboards = async (link?: string): Promise<MyStyleboards> => {
  const currentLink = link || STYLEBOARD_BASE

  const response = await request.get(currentLink, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  normaliseStyleboards(response.data.styleboards)
  return response.data
}

export const fetchPublicStyleboards = async (
  sortBy: PublicStyleboardSort,
  link?: string,
): Promise<MyStyleboards> => {
  const currentLink = link || `${PUBLIC_STYLEBOARD_BASE}?sort=${sortBy},desc`

  const response = await request.get(currentLink, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  normaliseStyleboards(response.data.styleboards)
  return response.data
}

export const fetchFollowStyleboards = async (link?: string): Promise<MyStyleboards> => {
  const currentLink = link || `${MY_STYLEBOARDS_BASE}?size=500`

  const response = await request.get(currentLink, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  normaliseStyleboards(response.data.styleboards)
  return response.data
}

export const fetchPopularStyleboards = async (): Promise<Styleboard[]> => {
  const response = await request.get(POPULAR_STYLEBOARDS_BASE, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  normaliseStyleboards(response.data)
  return response.data
}

export const fetchSimilarItems = async (
  styleboardId: string,
): Promise<FetchSimilarItemsResponse | HttpStatusCode> => {
  const response = await request.get(SIMILAR_ITEMS_BASE, {
    params: {
      styleboardId,
      apiKey: process.env.STYLEBOARD_API_KEY,
      isImageUrlFormatted: true,
    },
    ...extraConfig,
  })

  normaliseArticles(response.data.articles)
  return response.data
}

export const followStyleboard = async (styleboardId: string): Promise<HttpStatusCode> => {
  const response = await request.put(MY_STYLEBOARDS_BASE, {
    data: {
      id: styleboardId,
    },
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const unfollowStyleboard = async (styleboardId: string): Promise<HttpStatusCode> => {
  const response = await request.delete(`${MY_STYLEBOARDS_BASE}/${styleboardId}`, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const putStyleboard = async (
  id: string,
  title: string,
  about: string,
  status: StyleboardStatus,
): Promise<HttpStatusCode> => {
  const formData = { title, about, status }

  const response = await request.put(`${STYLEBOARD_BASE}/${id}`, {
    data: formData,
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const deleteStyleboard = async (styleboardId: string): Promise<HttpStatusCode> => {
  const response = await request.delete(`${STYLEBOARD_BASE}/${styleboardId}`, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const putItemToStyleboard = async (
  data: PutItemToStyleboardRequest,
): Promise<HttpStatusCode> => {
  const response = await request.put(STYLEBOARD_ITEMS, {
    data,
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const deleteItemFromStyleboard = async (
  styleboardId: string,
  itemId: string,
): Promise<HttpStatusCode> => {
  const response = await request.delete(`${STYLEBOARD_BASE}/${styleboardId}/items/${itemId}`, {
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const putProfile = async (author: Author): Promise<HttpStatusCode> => {
  const response = await request.put(`${USERS_BASE}/${author.id}`, {
    data: author,
    params: {
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    ...extraConfig,
  })

  return response.status
}

export const addImage = async (
  file: File,
  caption: string,
  type: ImageType,
  styleboardId?: string,
): Promise<HttpStatusCode> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await request.put(MEDIA_BASE, {
    data: formData,
    params: {
      type,
      styleboardId,
      ...(caption && { caption }),
      apiKey: process.env.STYLEBOARD_API_KEY,
    },
    shouldTrimData: false,
    ...extraConfig,
  })

  return response.status
}
